"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * @typedef {Object} Column
 * @property {string} header - Column header text
 * @property {string} accessor - Property name to access in data objects
 * @property {Function} [cell] - Optional custom cell renderer
 * @property {string} [className] - Optional additional CSS classes
 */

/**
 * @typedef {Object} PaginationProps
 * @property {number} currentPage - Current page number
 * @property {number} pageSize - Number of items per page
 * @property {number} totalItems - Total number of items
 * @property {Function} onPageChange - Function to call when page changes
 */

/**
 * @typedef {Object} TableProps
 * @property {Column[]} columns - Array of column definitions
 * @property {Array} data - Array of data objects
 * @property {PaginationProps} [pagination] - Optional pagination configuration
 * @property {string} [className] - Optional additional CSS classes
 */

/**
 * Reusable Table component with pagination
 * @param {TableProps} props - Component props
 * @property {boolean} [loading] - Optional loading indicator
 */

export default function Table({
  columns,
  data,
  pagination,
  className,
  loading = false,
}) {
  const { pageSize, totalItems, currentPage } = pagination;
  const totalPages = Math.ceil(totalItems / pageSize);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData =
  pagination && totalItems === data?.length
    ? data?.slice(startIndex, endIndex)
    : data || [];


  // Helper function to combine class names
  const cn = (...classes) => classes.filter(Boolean).join(" ");

  return (
    <div className={cn("w-full", className)}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              {columns?.map((column, index) => (
                <th
                  key={index}
                  className={cn(
                    "px-8 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                    column.className
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center px-8 py-4 text-gray-500"
                >
                  Loading...
                </td>
              </tr>
            ) : paginatedData?.length > 0 ? (
              paginatedData?.map((row, rowIndex) => {
                const globalIndex = (currentPage - 1) * pageSize + rowIndex;
                return (
                  <tr
                    key={rowIndex}
                    className="hover:bg-gray-50 transition-colors group"
                  >
                    {columns?.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={cn(
                          "px-8 py-2 whitespace-nowrap",
                          column.className
                        )}
                      >
                        {column.cell ? (
                          column.cell(
                            row[column.accessor] !== undefined
                              ? row[column.accessor]
                              : "N/A",
                            row,
                            globalIndex
                          )
                        ) : (
                          <div className="text-sm text-gray-600">
                            {row[column.accessor] !== undefined
                              ? row[column.accessor]
                              : "N/A"}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center px-8 py-4 text-gray-500"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between px-8 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing{" "}
            {Math.min(
              (pagination.currentPage - 1) * pagination.pageSize + 1,
              pagination.totalItems
            )}{" "}
            to{" "}
            {Math.min(
              pagination.currentPage * pagination.pageSize,
              pagination.totalItems
            )}{" "}
            of {pagination.totalItems} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() =>
                pagination.onPageChange(pagination.currentPage - 1)
              }
              disabled={pagination.currentPage <= 1}
              className="p-2 rounded-md border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-sm font-medium">
              Page {pagination.currentPage} of {totalPages}
            </div>
            <button
              onClick={() =>
                pagination.onPageChange(pagination.currentPage + 1)
              }
              disabled={pagination.currentPage >= totalPages}
              className="p-2 rounded-md border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
